import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { TransactionExtended, TransactionExtendedQuery, TransactionExtendedService } from '@fgb/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { formatDate } from '@angular/common';

@Component({
  selector: 'fgb-split-transaction-list',
  templateUrl: './split-transaction-list.component.html',
  styleUrls: ['./split-transaction-list.component.scss'],
  exportAs: 'splitTransaction',
})
export class SplitTransactionListComponent implements OnInit, OnDestroy {
  loading$: Observable<boolean>;
  transactions$: Observable<{ date: string; transactions: TransactionExtended[] }[]>;
  private _transactionsSubject = new BehaviorSubject<{ date: string; transactions: TransactionExtended[] }[]>([]);
  private _allTransactions: TransactionExtended[] = [];
  pageNo: number = 1;
  isLoadDisabled: boolean;
  _destroyed$ = new Subject<void>();
  @Input() pageSize: number = 10;
  @Input() dateFrom: Date;
  @Input() dateTo: Date;
  @Input() resetOnLoad: boolean = true;

  constructor(
    private transactionService: TransactionExtendedService,
    private transactionExtendedQuery: TransactionExtendedQuery
  ) {}

  ngOnInit(): void {
    // When navigating to other pages some unexpected issue occurs, this resets everything to default
    if (this.resetOnLoad) {
      this.reset();
    }

    this.transactions$ = this._transactionsSubject.asObservable();
    this.loading$ = this.transactionExtendedQuery.loading$;
  }

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  trackByFn(index: number, item: TransactionExtended) {
    return item.TransactionRef;
  }

  private _groupByDate(transactions: TransactionExtended[]): { date: string; transactions: TransactionExtended[] }[] {
    const sorted = [...transactions].sort(
      (a, b) => new Date(b.TransactionDateUTC).getTime() - new Date(a.TransactionDateUTC).getTime()
    );
    const uniqueDates = sorted
      .filter((val, idx, arr) =>
        arr.findIndex(i => this._formatDate(i.TransactionDateUTC) === this._formatDate(val.TransactionDateUTC)) === idx
      )
      .map(i => this._formatDate(i.TransactionDateUTC));
    return uniqueDates.map(date => ({
      date,
      transactions: sorted.filter(i => this._formatDate(i.TransactionDateUTC) === date),
    }));
  }

  private _formatDate(date: string | Date): string {
    return formatDate(date, 'MMM d, y', 'en-US');
  }

  _search(from: Date, to: Date, pageNo?: number, pageSize?: number): void {
    this.pageNo = pageNo || this.pageNo;
    this.pageSize = pageSize || this.pageSize;
    this.transactionService
      .search(1, from, to, this.pageNo, this.pageSize)
      .pipe(takeUntil(this._destroyed$))
      .subscribe((res: TransactionExtended[]) => {
        this.isLoadDisabled = !res || res.length < this.pageSize;
        if (pageNo && pageNo > 1) {
          this._allTransactions = [...this._allTransactions, ...(res || [])];
        } else {
          this._allTransactions = res || [];
        }
        this._transactionsSubject.next(this._groupByDate(this._allTransactions));
      });
  }

  search(): void {
    this._search(this.dateFrom, this.dateTo);
  }

  searchByDate(dateFrom: Date, dateTo: Date): void {
    this.dateFrom = dateFrom || this.dateFrom;
    this.dateTo = dateTo || this.dateTo;
    this._search(dateFrom, dateTo);
  }

  loadMore(): void {
    this._search(this.dateFrom, this.dateTo, ++this.pageNo);
  }

  reset(): void {
    this.pageNo = 1;
    this.isLoadDisabled = false;
    this._allTransactions = [];
    this._transactionsSubject.next([]);
    this.transactionService.reset();
  }
}
